import { dataService } from '../services/dataService'
import { TRANSITION_TYPE_LABELS, TransitionType } from '../models/TransitionType'

// Функция для поиска должности по ID
export const findPositionById = async (positionId) => {
  const positions = await dataService.getPositions()
  return positions.find(pos => pos.id === positionId) || null
}

// Функция для получения всех должностей в виде плоского массива
export const getAllPositions = async () => {
  return await dataService.getPositions()
}

// Функция для поиска должностей по названию
export const searchPositions = async (searchTerm) => {
  return await dataService.searchPositions(searchTerm)
}

// Функция для группировки должностей по уровням
export const groupPositionsByLevel = async () => {
  const allPositions = await getAllPositions()
  const grouped = {}
  
  allPositions.forEach(position => {
    if (!grouped[position.level]) {
      grouped[position.level] = []
    }
    grouped[position.level].push(position)
  })
  
  return grouped
}

// Функция для получения карьерных путей
// Источник: GET /positions/:id → transitions_from (только исходящие переходы)
export const getCareerPaths = async (positionId) => {
  const positionDetails = await dataService.getPosition(positionId)
  return Array.isArray(positionDetails?.transitions_from)
    ? positionDetails.transitions_from
    : []
}

// Функция для определения типа перехода
export const getTransitionType = (fromPosition, toPosition) => {
  if (!fromPosition || !toPosition) return null
  
  // Если тот же отдел и уровень выше - вертикальный рост
  if (fromPosition.department_id === toPosition.department_id && toPosition.level < fromPosition.level) {
    return TRANSITION_TYPE_LABELS[TransitionType.VERTICAL]
  }
  
  // Если тот же уровень но разные отделы - горизонтальные переходы
  if (fromPosition.level === toPosition.level && fromPosition.department_id !== toPosition.department_id) {
    return TRANSITION_TYPE_LABELS[TransitionType.HORIZONTAL]
  }
  
  // Все остальные случаи - межфункциональные
  return TRANSITION_TYPE_LABELS[TransitionType.CROSS_FUNCTIONAL]
}

// Функция для получения статистики организации
export const getOrganizationStats = async () => {
  try {
    const [departments, allPositions] = await Promise.all([
      dataService.getDepartmentsWithPositions(),
      dataService.getPositions()
    ])
    
    const positionsByLevel = await groupPositionsByLevel()
    
    return {
      totalDepartments: departments.length,
      totalPositions: allPositions.length,
      positionsByLevel,
      departmentSizes: departments.map(dept => ({
        name: dept.name,
        count: dept.positions.length
      }))
    }
  } catch (error) {
    console.error('Ошибка получения статистики:', error)
    return {
      totalDepartments: 0,
      totalPositions: 0,
      positionsByLevel: {},
      departmentSizes: []
    }
  }
}

// Функция обогащения должности данными о карьерных путях
export const enrichPositionWithCareerPaths = async (position) => {
  try {
    // Берём только исходящие переходы из API /positions/:id
    const positionDetails = await dataService.getPosition(position.id)
    const transitions = Array.isArray(positionDetails?.transitions_from)
      ? positionDetails.transitions_from
      : []

    // Нужны целевые позиции для уровня/отдела и определения типа перехода
    const allPositions = await dataService.getPositions()

    const careerPaths = transitions
      .filter(t => t?.from_position_id === position.id)
      .map(transition => {
        const targetPosition = allPositions.find(
          pos => pos.id === transition.to_position_id
        )

        // Фолбэки, если целевая позиция не найдена в общем списке
        const targetTitle = targetPosition?.title || transition.to_position_title || 'Неизвестная должность'
        const targetDepartment =
          targetPosition?.department_name ||
          targetPosition?.department ||
          transition.to_department_name ||
          '—'
        const targetLevel = targetPosition?.level ?? null

        // Берем тип перехода из БД с fallback логикой
        let transitionType = transition.transition_type;
        
        // Если тип перехода отсутствует, пустой или содержит только пробелы, используем fallback
        if (!transitionType || typeof transitionType !== 'string' || transitionType.trim() === '') {
          transitionType = targetPosition ? getTransitionType(position, targetPosition) : null;
        } else {
          // Очищаем от лишних пробелов
          transitionType = transitionType.trim();
        }

        return {
          id: targetPosition?.id || transition.to_position_id,
          title: targetTitle,
          department: targetDepartment,
          level: targetLevel,
          type: transitionType,
          requirements: transition.requirements && transition.requirements.trim() !== '' 
            ? transition.requirements.trim()
            : null
        }
      })
      .filter(Boolean)

    return {
      ...position,
      careerPaths
    }
  } catch (error) {
    console.error('Ошибка обогащения должности карьерными путями:', error)
    return {
      ...position,
      careerPaths: []
    }
  }
}

// Функция для валидации данных
export const validatePositionData = (position) => {
  const errors = []
  
  if (!position.id) errors.push('Отсутствует ID должности')
  if (!position.title) errors.push('Отсутствует название должности')
  if (!position.department_id) errors.push('Отсутствует отдел')
  if (!position.level || position.level < 1 || position.level > 5) {
    errors.push('Некорректный уровень должности (должен быть от 1 до 5)')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
} 